import React, { StrictMode, lazy, Suspense } from 'react';

import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { keepaliveSafeTransport } from '@ergeon/erg-utils-js/exports/sentry/keepaliveSafeTransport';

import { LEVEL, SENTRY_DSN } from '@shared/infrastructure/envs';
import { installRbacDenialWatcher } from '@shared/infrastructure/rbacDenials';
import RbacDenialBanner from '@shared/ui/RbacDenialBanner';

import '@shared/styles/tailwind.css';

const MOBILE_BREAKPOINT = 768;

const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

const loadApp = () => {
  if (isMobile()) {
    return import('./mobile/App');
  }
  return import('./desktop/App');
};

const App = lazy(loadApp);

// Never report from local development: a leaked DSN (via .env.local or an
// exported VITE_SENTRY_DSN, which Vite loads in every mode) would otherwise
// send dev-server noise to Sentry tagged environment=development.
const isDeployedEnv = LEVEL !== 'development';

if (SENTRY_DSN && isDeployedEnv) {
  Sentry.init({
    dsn: SENTRY_DSN,
    transport: keepaliveSafeTransport,
    integrations: [Sentry.replayIntegration()],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: LEVEL,
  });
}

installRbacDenialWatcher();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Suspense fallback={null}>
      <App />
    </Suspense>
    <RbacDenialBanner />
  </StrictMode>,
);
